import { useEffect, useRef, useSyncExternalStore } from "react";
import type { DocumentUploadResult } from "@mind/contracts/browser";
import { documentApi, type DocumentUploadInput, type DocumentUploadProgress } from "./documentApi.js";

export type DocumentUploadQueuePhase = "queued" | "hashing" | "uploading" | "submitted" | "error" | "canceled";

export type DocumentUploadQueueEntry = {
  readonly id: string;
  readonly file: File;
  readonly phase: DocumentUploadQueuePhase;
  readonly progress: DocumentUploadProgress | null;
  readonly result: DocumentUploadResult | null;
  readonly error: unknown | null;
};

export type DocumentUploadQueue = {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => readonly DocumentUploadQueueEntry[];
  readonly enqueue: (files: readonly File[]) => void;
  readonly retry: (id: string) => void;
  readonly cancel: (id: string) => void;
  readonly remove: (id: string) => void;
  readonly reset: (workspaceId: string) => void;
  readonly setUploadedHandler: (handler: ((result: DocumentUploadResult) => void | Promise<void>) | undefined) => void;
  readonly dispose: () => void;
};

type QueueOptions = {
  readonly workspaceId: string;
  readonly upload?: (input: DocumentUploadInput) => Promise<DocumentUploadResult>;
  readonly onUploaded?: (result: DocumentUploadResult) => void | Promise<void>;
};

function queueId(): string {
  return crypto.randomUUID();
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

export function createDocumentUploadQueue(options: QueueOptions): DocumentUploadQueue {
  let workspaceId = options.workspaceId;
  let entries: DocumentUploadQueueEntry[] = [];
  let running = false;
  let disposed = false;
  let uploadedHandler = options.onUploaded;
  const listeners = new Set<() => void>();
  const controllers = new Map<string, AbortController>();

  const notify = (): void => {
    for (const listener of listeners) listener();
  };
  const update = (id: string, change: Partial<DocumentUploadQueueEntry>): void => {
    let changed = false;
    entries = entries.map((entry) => {
      if (entry.id !== id) return entry;
      changed = true;
      return { ...entry, ...change };
    });
    if (changed) notify();
  };
  const pump = (): void => {
    if (disposed || running || workspaceId.length === 0) return;
    const next = entries.find((entry) => entry.phase === "queued");
    if (next === undefined) return;
    running = true;
    const controller = new AbortController();
    controllers.set(next.id, controller);
    update(next.id, { phase: "hashing", progress: null, error: null });
    const upload = options.upload ?? documentApi.upload;
    void upload({
      workspaceId,
      file: next.file,
      signal: controller.signal,
      onProgress: (progress) => update(next.id, { phase: "uploading", progress }),
    }).then((result) => {
      if (disposed) return;
      update(next.id, { phase: "submitted", progress: { loaded: next.file.size, total: next.file.size }, result, error: null });
      void Promise.resolve().then(() => uploadedHandler?.(result)).catch(() => undefined);
    }).catch((error: unknown) => {
      if (disposed) return;
      if (isAbortError(error) || controller.signal.aborted) {
        if (entries.find((entry) => entry.id === next.id)?.phase !== "queued") update(next.id, { phase: "canceled", error: null });
      } else {
        update(next.id, { phase: "error", error });
      }
    }).finally(() => {
      controllers.delete(next.id);
      running = false;
      pump();
    });
  };

  const queue: DocumentUploadQueue = {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot() {
      return entries;
    },
    enqueue(files) {
      if (disposed || workspaceId.length === 0 || files.length === 0) return;
      entries = entries.concat(files.map((file) => ({
        id: queueId(),
        file,
        phase: "queued" as const,
        progress: null,
        result: null,
        error: null,
      })));
      notify();
      pump();
    },
    retry(id) {
      if (disposed) return;
      const entry = entries.find((candidate) => candidate.id === id);
      if (entry === undefined || (entry.phase !== "error" && entry.phase !== "canceled")) return;
      update(id, { phase: "queued", progress: null, result: null, error: null });
      pump();
    },
    cancel(id) {
      if (disposed) return;
      const entry = entries.find((candidate) => candidate.id === id);
      if (entry === undefined || entry.phase === "submitted" || entry.phase === "error" || entry.phase === "canceled") return;
      controllers.get(id)?.abort();
      update(id, { phase: "canceled", error: null });
    },
    remove(id) {
      if (disposed) return;
      controllers.get(id)?.abort();
      const next = entries.filter((entry) => entry.id !== id);
      if (next.length === entries.length) return;
      entries = next;
      notify();
      pump();
    },
    reset(nextWorkspaceId) {
      if (disposed || nextWorkspaceId === workspaceId) return;
      const hadActiveUpload = controllers.size > 0;
      for (const controller of controllers.values()) controller.abort();
      controllers.clear();
      workspaceId = nextWorkspaceId;
      entries = [];
      running = hadActiveUpload;
      notify();
    },
    setUploadedHandler(handler) {
      uploadedHandler = handler;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const controller of controllers.values()) controller.abort();
      controllers.clear();
      listeners.clear();
    },
  };

  return queue;
}

export function useDocumentUploadQueue(
  workspaceId: string,
  onUploaded?: (result: DocumentUploadResult) => void | Promise<void>,
): {
  readonly entries: readonly DocumentUploadQueueEntry[];
  readonly enqueue: (files: readonly File[]) => void;
  readonly retry: (id: string) => void;
  readonly cancel: (id: string) => void;
  readonly remove: (id: string) => void;
} {
  const queueRef = useRef<DocumentUploadQueue | null>(null);
  const initialOptions: QueueOptions = onUploaded === undefined ? { workspaceId } : { workspaceId, onUploaded };
  if (queueRef.current === null) queueRef.current = createDocumentUploadQueue(initialOptions);
  const queue = queueRef.current;
  useEffect(() => {
    queue.reset(workspaceId);
    queue.setUploadedHandler(onUploaded);
    return () => queue.reset("");
  }, [onUploaded, queue, workspaceId]);
  const entries = useSyncExternalStore(queue.subscribe, queue.getSnapshot, queue.getSnapshot);
  return { entries, enqueue: queue.enqueue, retry: queue.retry, cancel: queue.cancel, remove: queue.remove };
}
